import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminProductAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import ProductUploadModal from '../components/ProductUploadModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import '../styles/Products.css';

// Helper function to convert Google Drive sharing links to direct image URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return '';

  // Trim the URL to remove any whitespace
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return '';

  // Check if it's a Google Drive link
  const driveMatch = trimmedUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch && driveMatch[1]) {
    // Convert to direct download format
    return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  }

  // Return original URL if it's not a Google Drive link
  return trimmedUrl;
};

const Products = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewMode, setViewMode] = useState("table"); // 'grid' or 'table'
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    brand: ''
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchProducts = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: 12,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category }),
        ...(filters.brand && { brand: filters.brand })
      };

      const response = await adminProductAPI.getAll(params);

      setProducts(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPage(1);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleProductDeleted = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
    showAlert('Product deleted successfully!', 'success');
    fetchProducts();
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      if (currentStatus === 'Active') {
        await adminProductAPI.deactivate(id);
      } else {
        await adminProductAPI.activate(id);
      }

      fetchProducts();
    } catch (err) {
      console.error('Error updating status:', err);
      showAlert(err.response?.data?.message || 'Failed to update product status', 'error');
    }
  };

  const handleUploadSuccess = () => {
    // setShowUploadModal(false);
    fetchProducts();
  };

  if (loading && products.length === 0) {
    return (
      <div className="products-container">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="products-container">
      <div className="products-header">
        <h1>Product Management</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowUploadModal(true)}>
            Upload Excel
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/products/new')}>
            + Add New Product
          </button>
        </div>
      </div>

      <div className="products-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={handleSearch}
            />
          </div>

          <div className="filters">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="toolbar-right">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
              </svg>
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {products.length === 0 ? (
        <div className="empty-state">
          <p>No products found</p>
          <button className="btn btn-primary" onClick={() => window.location.href = '/products/new'}>
            Create Your First Product
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="products-grid">
          {products.map(product => (
            <div key={product._id} className="product-card">
              <div className="product-image">
                {product.productImage ? (
                  <img
                    src={convertGoogleDriveUrl(product.productImage)}
                    alt={product.productName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="no-image">Image failed to load</div>';
                    }}
                  />
                ) : (
                  <div className="no-image">No Image</div>
                )}
                <span className={`status-badge ${product.status?.toLowerCase()}`}>
                  {product.status}
                </span>
              </div>

              <div className="product-info">
                <h3>{product.productName}</h3>
                <p className="catalog-number">{product.catalogNumber}</p>

                {product.productCategory && (
                  <span className="category-badge">
                    {product.productCategory}
                  </span>
                )}

                {product.brandName && (
                  <span className="brand-badge">
                    {product.brandName}
                  </span>
                )}
              </div>

              <div className="product-actions">
                <button
                  className="btn btn-sm btn-view"
                  onClick={() => navigate(`/products/${product._id}`)}
                  title="View"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  View
                </button>
                <button
                  className="btn btn-sm btn-edit"
                  onClick={() => navigate(`/products/${product._id}/edit`)}
                  title="Edit"
                >
                  Edit
                </button>
                <button
                  className={`btn btn-sm ${product.status === 'Active' ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => handleStatusToggle(product._id, product.status)}
                  title={product.status === 'Active' ? 'Deactivate' : 'Activate'}
                >
                  {product.status === 'Active' ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDeleteClick(product)}
                  title="Delete"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Catalog Number</th>
                <th>Product Name</th>
                <th>Category</th>
                <th>Brand</th>
                <th>Pack Size</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product._id}>
                  <td>
                    <div className="table-product-image">
                      {product.productImage ? (
                        <img
                          src={convertGoogleDriveUrl(product.productImage)}
                          alt={product.productName}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="no-image-small">Image failed to load</div>';
                          }}
                        />
                      ) : (
                        <div className="no-image-small">No Image</div>
                      )}
                    </div>
                  </td>
                  <td className="catalog-number-cell">{product.catalogNumber}</td>
                  <td className="product-name-cell">{product.productName}</td>
                  <td>
                    {product.productCategory && (
                      <span className="category-badge">
                        {product.productCategory}
                      </span>
                    )}
                  </td>
                  <td>
                    {product.brandName && (
                      <span className="brand-badge">
                        {product.brandName}
                      </span>
                    )}
                  </td>
                  <td>{product.packSize || '-'}</td>
                  <td>
                    <span className={`btn`}>
                      {product.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="btn btn-sm btn-view"
                        onClick={() => window.location.href = `/products/${product._id}`}
                        title="View"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-edit"
                        onClick={() => window.location.href = `/products/${product._id}/edit`}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        className={`btn btn-sm ${product.status === 'Active' ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleStatusToggle(product._id, product.status)}
                        title={product.status === 'Active' ? 'Deactivate' : 'Activate'}
                      >
                        {product.status === 'Active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteClick(product)}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <ProductUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {showDeleteModal && (
        <DeleteConfirmModal
          item={productToDelete}
          itemType="product"
          onClose={() => {
            setShowDeleteModal(false);
            setProductToDelete(null);
          }}
          onDeleted={handleProductDeleted}
          deleteFunction={adminProductAPI.delete}
        />
      )}
    </div>
  );
};

export default Products;
